// Cotización con reparto: barra del precio neto partida en hoy (enganche y contado) y lo financiado.
// El precio neto con promo de venta va tachado → neto en verde con el nombre de la promo.
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import {
    SALE_PROMOTION_FALLBACK,
    SalePromotionPrice,
    SalePromotionTags,
} from '@/shared/patterns/SalePromotion';
import type { QuoteTable } from '../quoteBreakdown';
import type { LineSlots } from './LineLabels';

/** Lugares para las piezas de Promociones en la Cotización. */
export type QuoteTableSlots = LineSlots;

const money = (cents: number): string => formatMXN(cents / 100);

/** Precio de la venta antes de descuentos si alguna línea trae uno; `null` sin promo de venta. */
export function saleOriginalCentsOf(table: QuoteTable): number | null {
    const discounted = table.rows.some((r) => !r.gift && r.originalCents !== null);
    if (!discounted) return null;
    return table.rows.reduce((sum, r) => sum + (r.gift ? 0 : (r.originalCents ?? r.netCents)), 0);
}

function PromotionTag({ table, slots }: { table: QuoteTable; slots: QuoteTableSlots }) {
    const row = table.rows.find((r) => !r.gift && r.originalCents !== null);
    if (row === undefined) return null;
    const refs = row.line.promotions ?? (row.line.promotion ? [row.line.promotion] : []);
    const names = refs.map((ref) =>
        slots.promotionLabel ? (
            <span key={ref.promotionId}>
                {slots.promotionLabel({
                    ...ref,
                    format: (n) => n,
                    fallback: SALE_PROMOTION_FALLBACK,
                })}
            </span>
        ) : (
            SALE_PROMOTION_FALLBACK
        ),
    );
    if (names.length === 0 && row.discountCode === undefined) names.push(SALE_PROMOTION_FALLBACK);
    return (
        <SalePromotionTags
            names={names}
            {...(row.discountCode !== undefined && { code: row.discountCode })}
        />
    );
}

const DOWN_TONE = 'bg-foreground';
const CASH_TONE = 'bg-muted-foreground/40';
const FINANCED_TONE = 'bg-promo';

/** Porcentaje del precio neto, para el ancho de cada segmento. */
const pctOf = (cents: number, totalCents: number): string =>
    `${String(totalCents > 0 ? (cents / totalCents) * 100 : 0)}%`;

function Breakdown({
    tone,
    label,
    cents,
    testId,
}: {
    tone: string;
    label: string;
    cents: number;
    testId: string;
}) {
    return (
        <div data-testid={testId} className="flex items-center gap-1.5">
            <span aria-hidden className={cn('size-2 flex-none rounded-full', tone)} />
            <span>
                {label} <span className="tabular-nums">{money(cents)}</span>
            </span>
        </div>
    );
}

export function QuoteSaleTotals({
    table,
    downPaymentCents,
    financedCents,
    slots = {},
}: {
    table: QuoteTable;
    downPaymentCents: number;
    /** Monto a financiar de la cotización: lo financiable menos el enganche. */
    financedCents: number;
    slots?: QuoteTableSlots;
}) {
    const originalCents = saleOriginalCentsOf(table);
    const netCents = table.netCents;
    const todayCents = downPaymentCents + table.cashCents;
    const hasCash = table.cashCents > 0;
    return (
        <div data-testid="quote-totals" className="flex flex-col gap-2 text-[13px]">
            <div
                data-testid="quote-total-net"
                className="flex flex-wrap items-baseline justify-end gap-x-2 text-[12px] text-muted-foreground"
            >
                {originalCents !== null && <PromotionTag table={table} slots={slots} />}
                <span>
                    de{' '}
                    {originalCents !== null ? (
                        <SalePromotionPrice originalCents={originalCents} netCents={netCents} />
                    ) : (
                        <span className="tabular-nums">{money(netCents)}</span>
                    )}
                </span>
            </div>

            {/* Barra del 100 % del precio neto: hoy (enganche y contado) y lo financiado. */}
            <div
                aria-hidden
                data-testid="quote-total-bar"
                className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
            >
                <span className={DOWN_TONE} style={{ width: pctOf(downPaymentCents, netCents) }} />
                {hasCash && (
                    <span
                        className={CASH_TONE}
                        style={{ width: pctOf(table.cashCents, netCents) }}
                    />
                )}
                <span className={FINANCED_TONE} style={{ width: pctOf(financedCents, netCents) }} />
            </div>

            <dl className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
                <div data-testid="quote-total-today" className="flex min-w-0 flex-col">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                        Hoy en caja
                    </dt>
                    <dd className="text-[22px] font-bold tabular-nums">{money(todayCents)}</dd>
                    {hasCash && (
                        <dd className="flex flex-col gap-0.5 text-[11.5px] text-muted-foreground">
                            <Breakdown
                                tone={DOWN_TONE}
                                label="Enganche"
                                cents={downPaymentCents}
                                testId="quote-total-down"
                            />
                            <Breakdown
                                tone={CASH_TONE}
                                label="Contado"
                                cents={table.cashCents}
                                testId="quote-total-cash"
                            />
                        </dd>
                    )}
                </div>
                <div
                    data-testid="quote-total-financed"
                    className="ml-auto flex min-w-0 flex-col items-end"
                >
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                        Financias
                    </dt>
                    <dd className="text-[22px] font-bold tabular-nums text-promo">
                        {money(financedCents)}
                    </dd>
                </div>
            </dl>
        </div>
    );
}
